import { userTokenRefreshCreate } from "@innoclouds/api";
import { captureException } from "@sentry/vue";
import { AxiosHeaders, type AxiosInstance } from "axios";
import { decodeJwt } from "jose";
import { SESSION_REPLACED_FLAG, TOKEN_KEYS, useAuth } from "#/composables/useAuth";
import { useNotifications } from "#/ui/notifications";
import clearLocalStorage from "#/utils/clearLocalStorage";

declare module "axios" {
	export interface InternalAxiosRequestConfig {
		withAuth?: boolean;
		_retry?: boolean;
	}
}

const validateJwt = (jwt: string) => {
	try {
		const decoded = decodeJwt(jwt);
		return { valid: true, isExpired: decoded.exp && decoded.exp < Date.now() / 1000 };
	} catch {
		return { valid: false, isExpired: false };
	}
};

const tryToRefreshToken = async () => {
	const { refreshToken } = useAuth();
	if (!refreshToken.value) return false;

	const jwt = validateJwt(refreshToken.value);
	if (!jwt.valid || jwt.isExpired) return false;

	try {
		const response = await userTokenRefreshCreate({ refresh: refreshToken.value }, { withAuth: false });
		if (response.access) return response;

		return false;
	} catch (e) {
		console.warn("[api.interceptors.auth]", "Failed to refresh token", e);
		captureException(e, { tags: { source: "api.interceptors.auth.tryToRefreshToken" }, level: "warning" });
		return false;
	}
};

const logout = (mode: "manual" | "device" = "manual") => {
	const auth = useAuth();
	// @TODO: Reset user store and other stores
	clearLocalStorage(["user", "cart", "bonus", TOKEN_KEYS.ACCESS, TOKEN_KEYS.REFRESH]);
	sessionStorage.removeItem("is_adult");
	auth.clear();

	const url = new URL(window.location.href);
	url.searchParams.set("logout", mode);
	window.location.href = url.toString();
};

export const useAuthInterceptor = (instance: AxiosInstance) => {
	const notifications = useNotifications();

	instance.interceptors.request.use(async (req) => {
		const { accessToken, setTokens } = useAuth();
		if (!accessToken.value || req.withAuth === false) return req;

		const jwt = validateJwt(accessToken.value);
		if (!jwt.valid) return req;
		if (jwt.isExpired) await tryToRefreshToken().then(setTokens);

		if (!req.headers) req.headers = new AxiosHeaders();
		if (accessToken.value) req.headers.Authorization = `Bearer ${accessToken.value}`;

		return req;
	});

	instance.interceptors.response.use(
		(response) => response,
		async (error) => {
			const auth = useAuth();
			const originalRequest = error.config;

			if (error.response?.status === 403 && error?.response?.data?.extra?.action === "SESSION_REPLACED") {
				const alreadyHandled = !!sessionStorage.getItem(SESSION_REPLACED_FLAG);
				sessionStorage.setItem(SESSION_REPLACED_FLAG, "1");
				auth.clear();
				if (originalRequest?.headers) delete originalRequest.headers.Authorization;
				if (alreadyHandled) return Promise.reject(error);

				if (error?.response?.data?.user_friendly_message) notifications.error(error?.response?.data?.user_friendly_message);
				logout("device");
				return Promise.reject(error);
			}

			if (originalRequest?.withAuth === false) return Promise.reject(error);

			if ([401, 403].includes(error.response?.status) && !originalRequest?._retry) {
				originalRequest._retry = true;

				const tokens = await tryToRefreshToken().then(auth.setTokens);
				if (!tokens) {
					auth.clear();
					delete originalRequest.headers.Authorization;
					return instance(originalRequest);
				}

				originalRequest.headers.Authorization = `Bearer ${tokens.access}`;
				return instance(originalRequest);
			}

			if (error.response?.status === 401 && error?.response?.data?.code === "token_not_valid") auth.clear();

			return Promise.reject(error);
		},
	);
};
